import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DEFAULT_LEARNING_LANGUAGE,
  LANGUAGE_DIALECTS,
  LEARNING_LANGUAGES,
  baseLearningLanguage,
  toLearningLanguage,
} from "@/constants/langcraftLanguages";
import { cn } from "@/lib/utils";

const STANDARD = "standard";

// Language dropdown plus an Accent dropdown (LangCraft dialects) for languages
// that have them. `value` / `onChange` use a single code: "en", "en-gb", …
// `allowedCodes` limits the choices (e.g. onboarding starter collections).
const LanguagePicker = ({
  id,
  value,
  onChange,
  allowedCodes,
  triggerClassName,
  layout = "stack",
}) => {
  const code = toLearningLanguage(value) || DEFAULT_LEARNING_LANGUAGE;
  const base = baseLearningLanguage(code) || DEFAULT_LEARNING_LANGUAGE;
  const isAllowed = (candidate) => !allowedCodes || allowedCodes.has(candidate);

  const languages = LEARNING_LANGUAGES.filter(
    (language) =>
      isAllowed(language.code) ||
      (LANGUAGE_DIALECTS[language.code] || []).some((dialect) => isAllowed(dialect.code))
  );
  const dialects = (LANGUAGE_DIALECTS[base] || []).filter((dialect) => isAllowed(dialect.code));
  const showStandard = isAllowed(base);
  const accentValue = code === base ? STANDARD : code;

  const handleLanguage = (nextBase) => {
    if (isAllowed(nextBase)) {
      onChange(nextBase);
      return;
    }
    // A language whose plain form isn't allowed: pick its first allowed accent.
    const firstDialect = (LANGUAGE_DIALECTS[nextBase] || []).find((dialect) =>
      isAllowed(dialect.code)
    );
    onChange(firstDialect ? firstDialect.code : nextBase);
  };

  const trigger = cn(
    "h-11 w-full rounded border border-line bg-elevated px-3 text-sm text-fg",
    triggerClassName
  );

  return (
    <div
      className={cn(
        "grid gap-2",
        layout === "row" && dialects.length > 0 && "sm:grid-cols-2"
      )}
    >
      <Select value={base} onValueChange={handleLanguage}>
        <SelectTrigger id={id} className={trigger} aria-label="Language">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {languages.map(({ code: languageCode, name, flag }) => (
            <SelectItem key={languageCode} value={languageCode}>
              {flag} {name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {dialects.length > 0 && (
        <Select
          value={accentValue}
          onValueChange={(next) => onChange(next === STANDARD ? base : next)}
        >
          <SelectTrigger className={trigger} aria-label="Accent">
            <span className="mr-1.5 shrink-0 text-fg-subtle">Accent:</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {showStandard && <SelectItem value={STANDARD}>Standard</SelectItem>}
            {dialects.map((dialect) => (
              <SelectItem key={dialect.code} value={dialect.code}>
                {dialect.flag} {dialect.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
};

export default LanguagePicker;
